import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
  Injector,
  afterNextRender,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { NavbarComponent } from './components/navbar/navbar.component';
import { AuthService } from '@auth0/auth0-angular';
import { LoadingComponent } from './components/loading/loading.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import {
  MatSidenav,
  MatSidenavContainer,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { AgGridAngular } from 'ag-grid-angular';
import { RolesStore } from './stores/roles.store';
import { filter, finalize, map, skip } from 'rxjs';
import { recordBrowserTimezoneOnce } from './shared/utils/browser-timezone';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContextFilterService } from './services/context-filter.service';
import { ErrorNotificationComponent } from './components/errors/error-notification/error-notification.component';
import { ModuleStore } from './stores/module.store';
import { AiChatWindowComponent } from './components/ai-chat-window/ai-chat-window.component';
import { AiChatUiService } from './services/ai-chat-ui.service';
import { SidenavUiService } from './services/sidenav-ui.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GurcMarkComponent } from './components/gurc-mark/gurc-mark.component';
import { AccessStore } from './stores/access.store';
import { isFeatureEnabled, FEATURE_FLAGS } from './shared/utils/feature-flags';
import { isPublicRoute } from './shared/utils/public-route.util';
import { isAiRouteUrl } from './shared/utils/ai-route.util';
import { ThemeService } from './services/theme.service';
import { UserStore } from './stores/user.store';
import { PERMISSION } from 'utils/permissions/permission-catalog';

@Component({
  selector: 'gcp-workspace-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    RouterOutlet,
    NavbarComponent,
    LoadingComponent,
    SidenavComponent,
    MatSidenavContainer,
    MatSidenav,
    MatSidenavContent,
    AgGridAngular,
    ErrorNotificationComponent,
    AiChatWindowComponent,
    MatIcon,
    MatTooltipModule,
    GurcMarkComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private auth = inject(AuthService);
  isAuthLoading = toSignal(this.auth.isLoading$, { initialValue: true });
  isAuthenticated = toSignal(this.auth.isAuthenticated$, {
    initialValue: false,
  });

  private destroyRef = inject(DestroyRef);

  private readonly router = inject(Router);
  private readonly contextFilterService = inject(ContextFilterService);

  /**
   * Whether the app booted on a public, login-free route (e.g. a share-link
   * issue form). Captured once at construction because the bootstrap calls
   * below cannot be un-fired; the `isPublicRoute` signal drives the template.
   */
  private readonly startedOnPublicRoute = isPublicRoute();

  /**
   * Drives the template: when true the shell chrome (navbar, sidenav, loading
   * gate, AI chat) is not rendered and the route owns the full viewport.
   */
  readonly onPublicRoute = signal(isPublicRoute());

  constructor() {
    // A public route has no session: every bootstrap call below targets an
    // authenticated endpoint and would fail with "Login required". Skip them
    // and clear the loading gate so the public page renders immediately.
    if (this.startedOnPublicRoute) {
      this.moduleStoreLoading.set(false);
    } else {
      this.bootstrapAuthenticatedShell();
    }

    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationError),
        map((evt) => evt as NavigationError),
        takeUntilDestroyed()
      )
      .subscribe((evt) => {
        if (evt.error instanceof Error && evt.error.name == 'ChunkLoadError') {
          window.location.assign(evt.url);
        }
      });

    this.isAiRoute.set(this.checkIsAiRoute(this.router.url));
    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((evt) => {
        const url = (evt as NavigationEnd).urlAfterRedirects;
        this.onPublicRoute.set(isPublicRoute(url));

        const nextIsAiRoute = this.checkIsAiRoute(url);
        this.isAiRoute.set(nextIsAiRoute);

        if (nextIsAiRoute) {
          this.aiChatUi.close();
        }
      });
  }

  /**
   * Org/user/role bootstrapping for the authenticated shell. Every call here
   * requires a session, so it runs only when the app did NOT boot on a public
   * route.
   */
  private bootstrapAuthenticatedShell(): void {
    // The loading flags gate a full-screen overlay (app.component.html), so
    // they must clear on failure too — `finalize` runs on error as well as
    // completion. Clearing only in `next` left the overlay up forever when
    // the backend was unreachable, covering even the /error page.
    this.moduleStore
      .loadAll()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.moduleStoreLoading.set(false))
      )
      .subscribe();

    this.contextFilterService.params$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadContextOverride.set(true);
        // Use requestAnimationFrame instead of setTimeout to avoid triggering
        // microtask-based change detection notifications in zoneless mode
        requestAnimationFrame(() => this.loadContextOverride.set(false));
        // Modules are org-scoped: withOrgEviction clears ModuleStore (entities + cache)
        // on a switch, so reload them for the newly selected org. params$ fires after
        // organizationId$ (which drives eviction), so the cache is a clean miss here.
        this.moduleStore
          .loadAll()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });

    // Effective permissions (roles overhaul §11.1) — nav, *appCan, and the
    // permission guards all read this one profile.
    this.accessStore
      .loadMyAccess()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.captureBrowserTimezone();

    this.rolesStore
      .getCurrentUserMemberships()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((memberships) =>
        // Drop a context org the user no longer belongs to, however it was
        // established: the backend refuses one outright now, so a stale value
        // (storage or a shared deep link) would 403 every request. Runs only
        // on success — a transient membership failure must never clear a
        // healthy context.
        this.contextFilterService.reconcileWithMemberships(
          memberships.filter((m) => m.active).map((m) => m.organizationId),
          this.userStore.currentUser()?.organizationId ?? null,
          this.accessStore.isUber()
        )
      );
  }

  /** Reminders need the user's zone; recorded once, calendar environments only. */
  private captureBrowserTimezone(): void {
    if (!isFeatureEnabled(FEATURE_FLAGS.CALENDAR_ENABLED)) {
      return;
    }
    recordBrowserTimezoneOnce(
      toObservable(this.userStore.currentUser, { injector: this.injector }),
      (user, timezone) =>
        this.userStore.editCurrentUser(user.id, {
          settings: { ...user.settings, timezone },
        })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  rolesStore = inject(RolesStore);
  accessStore = inject(AccessStore);
  moduleStore = inject(ModuleStore);
  moduleStoreLoading = signal(true);
  aiChatUi = inject(AiChatUiService);
  // Read here so the rail's width class can be applied to <mat-sidenav>, which
  // this component owns; the toggle itself lives in SidenavComponent.
  sidenavUi = inject(SidenavUiService);
  private readonly injector = inject(Injector);
  private readonly sidenavContainer = viewChild(MatSidenavContainer);

  /**
   * Angular Material sets the content's left margin from the drawer's measured
   * width, and only recomputes it on open/close/mode change or a viewport
   * resize — never when the drawer's width changes via CSS. Without this the
   * navbar and page content keep a 240px offset while the rail is 64px wide,
   * leaving a dead gap. afterNextRender guarantees the new width is committed
   * to the DOM before Material re-measures it.
   */
  private readonly railWidthEffect = effect(() => {
    this.sidenavUi.collapsed();
    afterNextRender(() => this.sidenavContainer()?.updateContentMargins(), {
      injector: this.injector,
    });
  });
  themeService = inject(ThemeService);
  userStore = inject(UserStore);

  private themeEffect = effect(() => {
    const user = this.userStore.currentUser();
    if (user) {
      const themeKey = user.settings?.theme || 'domaingrc-default';
      this.themeService.applyTheme(themeKey);
    }
  });
  isAiRoute = signal(false);
  // The ai.access entitlement is the per-user grant (§9.2); the feature
  // flag stays as the environment-level kill switch above it. Ubers pass
  // through can() as everywhere else.
  hasAiAccess = computed(
    () =>
      this.accessStore.can(PERMISSION.ai.access) &&
      isFeatureEnabled(FEATURE_FLAGS.AI_MODULE_ENABLED)
  );
  showFloatingChatLauncher = computed(
    () => !this.isAiRoute() && this.hasAiAccess()
  );

  private readonly document = inject(DOCUMENT);

  /**
   * Mirrors the launcher's template condition onto <body> so global styles can
   * keep content it would cover (the grid paging panel) clear of it. On body
   * rather than the host so grids inside dialogs (CDK overlay) are covered too.
   */
  private readonly launcherBodyClassEffect = effect((onCleanup) => {
    if (!this.isAuthenticated() || !this.showFloatingChatLauncher()) {
      return;
    }
    const body = this.document.body;
    body.classList.add('has-ai-launcher');
    onCleanup(() => body.classList.remove('has-ai-launcher'));
  });

  /**
   * Hint for the floating launcher, used as both its tooltip and its accessible
   * name so the two never drift. It names what the click does rather than the
   * control ("Toggle AI chat" told you neither which way it would go nor what
   * the icon was), and uses the chat window's own word for the agent.
   */
  floatingChatHint = computed(() =>
    this.aiChatUi.isOpen() ? 'Hide chat' : 'Ask the assistant'
  );

  loadContextOverride = signal(false);

  toggleFloatingChat(): void {
    this.aiChatUi.toggle();
  }

  private checkIsAiRoute(url: string): boolean {
    return isAiRouteUrl(url);
  }
}
